// @flow
import * as React from 'react';
import { Formik, Form, FormikActions, Field } from 'formik';
import * as Yup from 'yup';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// components
import { Container, Row, Col } from 'reactstrap';
import { TextField, FormikField } from 'components/api';
import FieldSwitch from 'components/atoms/FieldSwitch';
import Checkbox from 'components/atoms/Checkbox';
import Button from 'components/atoms/Button';

// Utils
import { isObjEmpty } from 'utils/varValidation';

import './Notifications.scss';

type PrivacySettings = {
  emailSpecialOffers: boolean,
  emailRemindUpcoming: boolean,
  emailRemindReschedule: boolean,
  emailRemindOverdue: boolean,
  phoneRemindUpcoming: boolean,
  phoneRemindReschedule: boolean,
  phoneRemindOverdue: boolean,
  textRemindUpcoming: boolean,
  textNotifyStatus: boolean
};

type Props = {
  email: string,
  emailPrivacyPreference: string,
  phonePrivacyPreference: string,
  textPrivacyPreference: string,
  privacySettings: PrivacySettings,
  personInfo: Object,
  closeModal: (show: boolean) => void,
  updateProfile: (personInfo: Object) => void,
  intl: IntlShape
};

type State = {
  privacySettings: PrivacySettings
};

type Values = { email: string };

const NotificationSchema = Yup.object().shape({
  email: Yup.string()
    .required()
    .email()
});

class Notifications extends React.Component<Props, State> {
  static block = 'notifications';

  constructor(props: Props) {
    super(props);
    (this: any).onSubmit = this.onSubmit.bind(this);
  }

  state = {
    privacySettings: this.props.privacySettings
  };

  initialValues = {
    email: this.props.email,
    emailSpecialOffers: this.state.privacySettings.emailSpecialOffers,
    emailRemindUpcoming: this.state.privacySettings.emailRemindUpcoming,
    emailRemindReschedule: this.state.privacySettings.emailRemindReschedule,
    emailRemindOverdue: this.state.privacySettings.emailRemindOverdue,
    phoneRemindUpcoming: this.state.privacySettings.phoneRemindUpcoming,
    phoneRemindReschedule: this.state.privacySettings.phoneRemindReschedule,
    phoneRemindOverdue: this.state.privacySettings.phoneRemindOverdue,
    textRemindUpcoming: this.state.privacySettings.textRemindUpcoming,
    textNotifyStatus: this.state.privacySettings.textNotifyStatus
  };

  toggleState(key: string) {
    this.setState(state => ({
      privacySettings: {
        ...state.privacySettings,
        [`${key}`]: !state.privacySettings[key]
      }
    }));
  }

  drawCheckbox = (name: string, label: string) => {
    const ns = Notifications.block;
    const isSelected = this.state.privacySettings[`${name}`];
    return (
      <div className={`${ns}__option`}>
        {isSelected > -1 && (
          <div>
            <Field
              name={name}
              render={({ field }) => (
                <Checkbox
                  ariaLabel={label}
                  value={isSelected}
                  type={'checkbox'}
                  isSelected={isSelected}
                  checked={isSelected}
                  onClick={() => this.toggleState(name)}
                  {...field}
                />
              )}
            />
            <label htmlFor={name} className={`${ns}__checkbox-label`}>
              {label}
            </label>
          </div>
        )}
      </div>
    );
  };

  drawNotificationOption = (name: string, label: string) => {
    const ns = Notifications.block;
    const { intl } = this.props;
    return (
      <Container className={`${ns}__item`}>
        <Row noGutters>
          <Col>
            <span className={`${ns}__label`}>{label}</span>
          </Col>
        </Row>
        <Row className={`${ns}__options`}>
          {this.drawCheckbox(
            `text${name}`,
            intl.formatMessage({ id: 'c8.common.text' })
          )}
          {this.drawCheckbox(
            `phone${name}`,
            intl.formatMessage({ id: 'c8.common.phone' })
          )}
          {this.drawCheckbox(
            `email${name}`,
            intl.formatMessage({ id: 'c8.common.email' })
          )}
        </Row>
      </Container>
    );
  };

  getUpdatedPersonInfo = (values: Values) => {
    const {
      personInfo,
      emailPrivacyPreference,
      textPrivacyPreference,
      phonePrivacyPreference
    } = this.props;
    const { contactInfo, ...personInfoProps } = personInfo;
    const {
      address,
      contactMethod,
      phoneNumbers,
      textConfirm,
      useEmail,
      // eslint-disable-next-line no-unused-vars
      ...contactInfoProps
    } = contactInfo;
    const {
      emailSpecialOffers,
      emailRemindUpcoming,
      emailRemindReschedule,
      emailRemindOverdue,
      phoneRemindUpcoming,
      phoneRemindReschedule,
      phoneRemindOverdue,
      textRemindUpcoming,
      textNotifyStatus
    } = this.state.privacySettings;
    const { email } = values;

    const newEmailPrivacyPreference = [
      +emailRemindUpcoming + '',
      +emailRemindReschedule + '',
      +emailRemindOverdue + '',
      +emailSpecialOffers + ''
    ]
      .concat(emailPrivacyPreference.slice(4))
      .join();

    const newPhonePrivacyPreference = [
      +phoneRemindUpcoming + '',
      +phoneRemindReschedule + '',
      +phoneRemindOverdue + ''
    ]
      .concat(phonePrivacyPreference.slice(3))
      .join();

    const newTextPrivacyPreference = [+textRemindUpcoming + '']
      .concat(textPrivacyPreference.slice(1, 6))
      .concat([+textNotifyStatus + ''])
      .join();

    return {
      contactInfo: {
        emailPrivacyPreference: newEmailPrivacyPreference,
        phonePrivacyPreference: newPhonePrivacyPreference,
        textPrivacyPreference: newTextPrivacyPreference,
        emailAddress: { email },
        address,
        contactMethod,
        phoneNumbers,
        textConfirm,
        useEmail
      },
      ...personInfoProps
    };
  };

  async onSubmit(values: Values, actions: FormikActions<Values>) {
    const { updateProfile, closeModal } = this.props;
    const { setSubmitting, setStatus } = actions;
    const personInfo = this.getUpdatedPersonInfo(values);
    const data = await updateProfile(personInfo);
    const success = data && data.success;
    setSubmitting(false);
    setStatus({ success });
    if (success) {
      closeModal(false);
    }
  }

  render() {
    const { emailSpecialOffers } = this.state.privacySettings;
    const { intl } = this.props;
    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={NotificationSchema}
        onSubmit={this.onSubmit}
        render={({ dirty, errors, isSubmitting }) => {
          const ns = Notifications.block;
          const disabled = !dirty || !isObjEmpty(errors);
          return (
            <Form className={ns}>
              <FormikField
                renderedComponent={TextField}
                name={'email'}
                tabIndex={0}
                aria-label={intl.formatMessage({
                  id: 'c8.aria.email.address.for.notifications',
                  defaultMessage: 'Email address for notifications'
                })}
              />
              <FormikField
                renderedComponent={FieldSwitch}
                name={'emailSpecialOffers'}
                label={intl.formatMessage({
                  id: 'c8.notifications.email.me.special.offers'
                })}
                className={`${ns}__field-switch`}
                checked={Boolean(emailSpecialOffers)}
                onClick={() => this.toggleState('emailSpecialOffers')}
              />
              <div
                className={`${ns}__grid-section`}
                tabIndex={0}
                role="group"
                aria-label={intl.formatMessage({
                  id: 'c8.aria.select.your.reminder.settings',
                  defaultMessage: 'Select your reminder settings'
                })}
              >
                <div className={`${ns}__title`}>
                  {intl.formatMessage({
                    id: 'c8.notifications.reminder.settings'
                  })}
                </div>
                {this.drawNotificationOption(
                  'RemindUpcoming',
                  intl.formatMessage({
                    id: 'c8.notifications.upcoming.appointments'
                  })
                )}
                {this.drawNotificationOption(
                  'RemindReschedule',
                  intl.formatMessage({
                    id: 'c8.notifications.reschedule.missed.appointments'
                  })
                )}
                {this.drawNotificationOption(
                  'RemindOverdue',
                  intl.formatMessage({
                    id: 'c8.notifications.overdue.services'
                  })
                )}
                {this.drawNotificationOption(
                  'NotifyStatus',
                  intl.formatMessage({
                    id: 'c8.notifications.approvals.for.services'
                  })
                )}
              </div>
              <Button
                disabled={disabled}
                type={'submit'}
                className={`sidebar-button--save-settings`}
                tabIndex={0}
              >
                {intl.formatMessage({ id: 'c8.common.save' })}
                {isSubmitting && (
                  <div className="ld ld-ring ld-spin-fast ml-2" />
                )}
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}

export default Notifications;



// WEBPACK FOOTER //
// ./src/components/pages/User/Notifications/Notifications.js